import { IJobQuestionnaireWithStats } from '../../types/questionnaire';
import Spinner from '../Spinner';

interface Props {
  questionnaires: IJobQuestionnaireWithStats[];
  selectedQuestionnaireId: string | null;
  onSelect: (questionnaireId: string) => void;
  isMobile: boolean;
  isLoading?: boolean;
}

const SectionHeader = ({ label }: { label: string }) => (
  <div
    style={{
      fontSize: '0.85rem',
      color: '#6c757d',
      fontWeight: 500,
      margin: '1rem 0 0.5rem 0',
    }}
  >
    {label}
  </div>
);

const QuestionnaireCard = ({
  item,
  isSelected,
  onClick,
}: {
  item: IJobQuestionnaireWithStats;
  isSelected: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        border: `1px solid ${isSelected ? '#2F86DD' : '#e0e0e0'}`,
        backgroundColor: isSelected ? '#2F86DD' : 'white',
        color: isSelected ? 'white' : '#1a1a1a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        cursor: 'pointer',
        marginBottom: '0.5rem',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      }}
    >
      <span
        style={{
          fontWeight: 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.questionnaireName}
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.8rem',
          color: isSelected ? 'white' : '#2F86DD',
          whiteSpace: 'nowrap',
        }}
      >
        <i className="fal fa-users" />
        {item.responseCount} responses
      </span>
    </div>
  );
};

const QuestionnaireSelectorList = ({
  questionnaires,
  selectedQuestionnaireId,
  onSelect,
  isMobile,
  isLoading,
}: Props) => {
  const active = questionnaires.filter(q => q.active);
  // Per VI-485 (directive 3): the Video Screener tab only
  // surfaces completed responses, so a previously-attached questionnaire
  // with zero responses is dead UI — hide it.
  const previous = questionnaires.filter(q => !q.active && q.responseCount > 0);

  if (isLoading && questionnaires.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          height: '100%',
          padding: '1rem',
          color: '#6c757d',
        }}
      >
        <Spinner size="xl"/>
      </div>
    );
  }

  if (questionnaires.length === 0) {
    return (
      <div style={{ padding: '1rem', color: '#6c757d' }}>
        <img 
          src="/svgs/no-results.svg" 
          alt="No questionnaires found" 
          draggable={false}
        />
        <div className="mt-2 mb-2 text-center">
          No questionnaires have been attached to this job.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '0.5rem 0' : '0' }}>
      <div
        style={{
          fontSize: '1rem',
          fontWeight: 500,
          color: '#1a1a1a',
          marginBottom: '0.25rem',
        }}
      >
        Questionnaires
      </div>

      {active.length > 0 && (
        <>
          <SectionHeader label="Active" />
          {active.map(item => (
            <QuestionnaireCard
              key={item.questionnaireId}
              item={item}
              isSelected={selectedQuestionnaireId === item.questionnaireId}
              onClick={() => onSelect(item.questionnaireId)}
            />
          ))}
        </>
      )}

      {previous.length > 0 && (
        <>
          <SectionHeader label="Previously Attached" />
          {previous.map(item => (
            <QuestionnaireCard
              key={item.questionnaireId}
              item={item}
              isSelected={selectedQuestionnaireId === item.questionnaireId}
              onClick={() => onSelect(item.questionnaireId)}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default QuestionnaireSelectorList;
